import { useEffect } from 'react';
import { FormikProvider, useField, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import FilledInBy from './common/FilledInBy';
import Property from './common/Property';

function CompetencyModalSelector() {
	const { t } = useTranslation();

	const { data } = graphql.useCompetenciesWithLevelsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const { localize } = useLocalize();

	const [field, , { setValue }] = useField('competency');

	const options =
		data?.competencies.edges
			.slice()
			.sort((a, b) => localize(a.node.title).text.localeCompare(localize(b.node.title).text))
			.map((edge) => ({
				label: localize(edge.node.title).text,
				value: edge.node.id,
			})) ?? [];

	const selectedOption = options.find((option) => option.value === field.value?.id);

	return (
		<Property title={t('common.settings.evaluationForms.properties.competency.title')}>
			{data ? (
				<Select
					value={selectedOption}
					onChange={(option) => {
						if (!option) {
							return;
						}

						setValue(data.competencies.edges.find((edge) => edge.node.id === option.value)?.node);
					}}
					options={options}
				/>
			) : (
				<Spinner isLoading size="18px" />
			)}
		</Property>
	);
}

export default function CompetencyModal(props: {
	item: graphql.EvaluationCompetencyItem;
	fillInBy: graphql.EvaluationFillInBy;
	onChange: (item: graphql.EvaluationCompetencyItem, fillInBy: graphql.EvaluationFillInBy) => void;
}) {
	const formik = useFormik({
		initialValues: {
			competency: props.item.competency,
			weight: props.item.weight,
			fillInBy: props.fillInBy,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		if (formik.values.competency?.id) {
			props.onChange(
				{
					...props.item,
					competency: formik.values.competency,
					weight: formik.values.weight,
				},
				formik.values.fillInBy,
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.competency?.id, formik.values.weight, formik.values.fillInBy]);

	return (
		<FormikProvider value={formik}>
			<CompetencyModalSelector />
			<FilledInBy />
		</FormikProvider>
	);
}
